import { memo } from 'react';
import { Button, Modal, ModalBody, ModalContent } from '@heroui/react';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { CHAT_MODAL_WIDTH_CLASS } from '../../config/constants';
import type { ChatAttachment } from '../../model/types';
import {
  downloadChatAttachment,
  openChatAttachment,
  useChatAttachmentMediaUrl,
} from '../../lib/attachmentLinks';
import { IconClose, IconDownload } from '../icons';

export type ChatMediaViewerModalProps = {
  isOpen: boolean;
  attachment: ChatAttachment | null;
  onClose: () => void;
};

export const ChatMediaViewerModal = memo(
  ({ isOpen, attachment, onClose }: ChatMediaViewerModalProps) => {
    useModalAutoFocus(isOpen);

    const canRenderMedia =
      attachment?.kind === 'image' || attachment?.kind === 'video';
    // Ссылка на 5 минут запрашивается при открытии; плееру этого хватает — дальше браузер держит поток
    const media = useChatAttachmentMediaUrl(attachment, isOpen && canRenderMedia);

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        placement="center"
        hideCloseButton
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] bg-[#101010] shadow-[0_24px_48px_rgba(0,0,0,0.32)]`,
          body: 'p-0',
          wrapper: 'px-2',
          backdrop: 'bg-black/60',
        }}
      >
        <ModalContent>
          <ModalBody>
            {/* Высота задана, а не только минимальная: иначе max-h-full у картинки не от чего
                считать, и вертикальное фото вылезало за экран */}
            <div className="relative flex h-[min(78vh,760px)] flex-col">
              <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white/95">
                    {attachment?.name ?? ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {attachment ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openChatAttachment(attachment)}
                        className="inline-flex h-10 items-center rounded-full bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadChatAttachment(attachment)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/15"
                        aria-label={`Download ${attachment.name}`}
                      >
                        <IconDownload className="h-5 w-5" />
                      </button>
                    </>
                  ) : null}
                  <Button
                    isIconOnly
                    radius="full"
                    variant="light"
                    className="h-10 w-10 min-w-10 bg-white/10 text-white hover:bg-white/15"
                    onPress={onClose}
                    aria-label="Close media viewer"
                  >
                    <IconClose className="h-[18px] w-[18px]" />
                  </Button>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 items-center justify-center px-4 pb-5 sm:px-6 sm:pb-6">
                {canRenderMedia && media.failed ? (
                  <div className="text-sm font-medium text-white/70">The file is not available</div>
                ) : canRenderMedia && !media.url ? (
                  <div className="text-sm font-medium text-white/70">Loading…</div>
                ) : canRenderMedia ? (
                  attachment.kind === 'image' ? (
                    <img
                      src={media.url ?? undefined}
                      alt={attachment.name}
                      className="max-h-full max-w-full rounded-[20px] object-contain"
                    />
                  ) : (
                    // Субтитров к чужому вложению взять неоткуда: его загрузил человек
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      src={media.url ?? undefined}
                      controls
                      autoPlay
                      playsInline
                      className="max-h-full max-w-full rounded-[20px] bg-black object-contain"
                    />
                  )
                ) : (
                  <div className="text-sm font-medium text-white/70">Unsupported media</div>
                )}
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);

ChatMediaViewerModal.displayName = 'ChatMediaViewerModal';
